import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent,
  Divider,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip
} from '@mui/material';
import {
  PeopleAlt as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as TransactionIcon,
  VerifiedUser as KycIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/formatters';

interface DashboardData {
  registredUsers: number;
  activeWallets: number;
  registredTransactions: number;
  pendingKycUsers: number;
  lastKycResponses: {
    userId: number;
    username: string;
    status: string;
    date: string;
  }[];
  lastTransactions: {
    userId: number;
    username: string;
    action: string;
    amount: string;
    date: string;
  }[];
}

// API function to fetch dashboard data
const fetchDashboardData = async (): Promise<DashboardData> => {
  const response = await api.get('/api/v1/admin-panel/dashboard', {
    headers: {
      'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
    }
  });

  return response.data.data;
};

const DashboardPage: React.FC = () => {
  // Use React Query to fetch dashboard data
  const { data: dashboardData, isLoading, error } = useQuery(
    'dashboard-data',
    fetchDashboardData,
    {
      refetchInterval: 30000,
      staleTime: 10000,
    }
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Дашборд
      </Typography>
      
      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Пользователи" 
            icon={<PeopleIcon fontSize="large" color="primary" />}
            value={isLoading || !dashboardData ? '--' : dashboardData.registredUsers}
            subtitle="Всего зарегистрировано"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Кошельки" 
            icon={<WalletIcon fontSize="large" color="secondary" />}
            value={isLoading || !dashboardData ? '--' : dashboardData.activeWallets}
            subtitle="Активных кошельков"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Транзакции" 
            icon={<TransactionIcon fontSize="large" sx={{ color: 'success.main' }} />}
            value={isLoading || !dashboardData ? '--' : dashboardData.registredTransactions}
            subtitle="Всего транзакций"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="KYC запросы" 
            icon={<KycIcon fontSize="large" sx={{ color: 'warning.main' }} />}
            value={isLoading || !dashboardData ? '--' : dashboardData.pendingKycUsers}
            subtitle="Ожидают проверки"
          />
        </Grid>
      </Grid>
      
      {/* Графики и таблицы */}
      <Grid container spacing={3}>
        {/* Последние транзакции */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Последние транзакции
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {isLoading || !dashboardData ? (
                // Показываем скелетоны во время загрузки
                [1, 2, 3, 4, 5].map((item: number) => (
                  <ListItem key={item} divider={item !== 5}>
                    <ListItemAvatar>
                      <Skeleton variant="circular" width={40} height={40} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Skeleton width="60%" />}
                      secondary={<Skeleton width="40%" />}
                    />
                    <Typography variant="body2" sx={{ minWidth: 80 }}>
                      <Skeleton width="100%" />
                    </Typography>
                  </ListItem>
                ))
              ) : (
                // Показываем реальные данные
                dashboardData.lastTransactions && dashboardData.lastTransactions.map((transaction, index) => (
                  <ListItem key={`${transaction.userId}-${index}`} divider={index !== dashboardData.lastTransactions.length - 1}>
                    <ListItemAvatar>
                      <Avatar>{transaction.username ? transaction.username.charAt(0).toUpperCase() : 'U'}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={transaction.username || 'Пользователь'}
                      secondary={formatDate(transaction.date)}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {transaction.amount}
                      </Typography>
                      <Chip 
                        label={transaction.action}
                        color={getTransactionTypeColor(transaction.action)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
        
        {/* Последние KYC запросы */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Последние KYC запросы
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List>
              {isLoading || !dashboardData ? (
                // Показываем скелетоны во время загрузки
                [1, 2, 3, 4, 5].map((item: number) => (
                  <ListItem key={item} divider={item !== 5}>
                    <ListItemAvatar>
                      <Skeleton variant="circular" width={40} height={40} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Skeleton width="60%" />}
                      secondary={<Skeleton width="40%" />}
                    />
                    <Typography variant="body2" sx={{ minWidth: 80 }}>
                      <Skeleton width="100%" />
                    </Typography>
                  </ListItem>
                ))
              ) : (
                // Показываем реальные данные
                dashboardData.lastKycResponses && dashboardData.lastKycResponses.map((request, index) => (
                  <ListItem key={`${request.userId}-${index}`} divider={index !== dashboardData.lastKycResponses.length - 1}>
                    <ListItemAvatar>
                      <Avatar>{request.username ? request.username.charAt(0).toUpperCase() : 'U'}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={request.username || 'Пользователь'}
                      secondary={formatDate(request.date)}
                    />
                    <Chip 
                      label={request.status}
                      color={getStatusColor(request.status)}
                      size="small"
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
        
        {/* Информация об ошибке - показываем только если есть ошибка */}
        {error && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom color="error">
                Ошибка загрузки данных
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography>
                Не удалось загрузить данные дашборда. Проверьте подключение к API и настройки admin secret.
                Ошибка: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Вспомогательная функция для определения цвета статуса
const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

// Вспомогательная функция для определения цвета типа транзакции
const getTransactionTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (type) {
    case 'deposit':
      return 'success';
    case 'withdrawal':
      return 'error';
    case 'transfer':
      return 'info';
    default:
      return 'default';
  }
};

interface StatCardProps {
  title: string;
  icon: React.ReactNode;
  value: string | number;
  subtitle: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, icon, value, subtitle }) => {
  return (
    <Card elevation={2}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="textSecondary">
            {title}
          </Typography>
          {icon}
        </Box>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default DashboardPage; 